import React, { useEffect, useState, useRef, useCallback } from "react";
import {
    Button,
    TextField,
    MenuItem,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    InputAdornment,
    Pagination,
    Box
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import SessionStorageService from "../../../services/StorageService";
import MemurIzinTalepAPI, {
    MemIzinTalepListRequest,
    MemIzinTalepGetRequest,
    PerIzinTuruTanimiSaatListRequest,
    PerIzinTuruTnmDtySaatLstRequest,
    PerIzinTuruTanimiDetayGetRequest,
    MemIzinTalepDetayListRequest,
    MemIzinDurumListRequest,
    MemIzinTalepSaveRequest,
    MemIzinTalepDurumUpdRequest,
    PerSicilBilgiOzelListRequest,
    MemIzinSuresiBulRequest
} from '../../../apis/MemurIzinTalepAPI';
import IsciIzinTalepAPI, {
    IsmIzinTalepListRequest,
    IsmIzinTalepGetRequest,
    IsmIzinTalepDetayListRequest,
    IsmIzinDurumListRequest,
    IsmIzinTalepSaveRequest,
    IsmIzinTalepDurumUpdRequest,
    IsmIzinSuresiBulRequest
} from '../../../apis/IsciIzinTalepAPI';
import { useLocation, useNavigate } from "react-router-dom";
import TableComponent from "../../../components/OB/TableComponent";
import { toast } from "react-toastify";
import IzinTalepRaporAPI from "../../../apis/OB/Rapor/IzinTalepRaporAPI";
import { openFileInNewTab } from "../../../helpers/openFileNewTab";

const initialValues = {
    sqId: null as number | null,
    izinTuru: "",
    izinTuruId: null as number | null,
    izinTuruDetay: "",
    izinTuruDetayId: null as number | null,
    izinTalepTarihi: dayjs(),
    izinBaslangicTarihi: dayjs(),
    izinBitisTarihi: dayjs(),
    izinBaslangicSaat: null as string | null,
    izinBitisSaat: null as string | null,
    kullanilanIzinSuresi: "",
    yolIzni: "",
    toplamIzin: "",
    iseBaslamaTarihi: dayjs().add(1, 'day'),
    izinDurumu: 0,
    izinAciklama: "",
    izinAdresi: "",
    redAciklama: "",
    vekaletEdenId: null as number | null,
    vekaletEden: "",
    vekaletEdenUnvan: "",
    onayaSunanId: null as number | null,
    onayaSunan: "",
    onayaSunanGorevId: null as number | null,
    onayaSunanUnvan: "",
    uygunGorenId: null as number | null,
    uygunGoren: "",
    uygunGorenGorevId: null as number | null,
    uygunGorenUnvan: "",
    onaylayanId: null as number | null,
    onaylayan: "",
    onaylayanGorevId: null as number | null,
    onaylayanUnvan: "",
    isSaatlikIzin: false,
    isYillikIzin: false
};

// Validation schema - isMemurUser parametresi ile dinamik
const getValidationSchema = (isMemurUser: boolean) => Yup.object().shape({
    izinTuruId: Yup.number().nullable().required("İzin Türü seçilmelidir"),
    izinTuruDetayId: Yup.number().nullable().required("İzin Türü Detay seçilmelidir"),
    izinTalepTarihi: Yup.mixed().required("İzin Talep Tarihi zorunludur"),
    izinBaslangicTarihi: Yup.mixed().required("İzin Başlangıç Tarihi zorunludur"),
    izinBitisTarihi: Yup.mixed().required("İzin Bitiş Tarihi zorunludur"),
    iseBaslamaTarihi: Yup.mixed().nullable().required("İşe Başlama Tarihi zorunludur"),
    izinAdresi: Yup.string().required("İzin Adresi zorunludur"),
    izinBaslangicSaat: Yup.string().nullable().when('isSaatlikIzin', {
        is: true,
        then: (schema) => schema
            .required("Saatlik izin için başlangıç saati zorunludur")
            .matches(/^([01]\d|2[0-3]):([0-5]\d)$/, "Geçerli bir saat formatı giriniz (HH:mm)"),
        otherwise: (schema) => schema.nullable()
    }),
    izinBitisSaat: Yup.string().nullable().when(['isSaatlikIzin', 'izinBaslangicSaat'], {
        is: (isSaatlikIzin: boolean, izinBaslangicSaat: string) => isSaatlikIzin && izinBaslangicSaat,
        then: (schema) => schema
            .required("Saatlik izin için bitiş saati zorunludur")
            .matches(/^([01]\d|2[0-3]):([0-5]\d)$/, "Geçerli bir saat formatı giriniz (HH:mm)")
            .test('is-after', 'Bitiş saati başlangıç saatinden sonra olmalıdır', function(value) {
                const { izinBaslangicSaat } = this.parent;
                if (!value || !izinBaslangicSaat) return true;
                return value > izinBaslangicSaat;
            }),
        otherwise: (schema) => schema.nullable()
    }),
    // Yol İzni - Sadece Memur için zorunlu
    yolIzni: Yup.string().when('isYillikIzin', {
        is: true,
        then: (schema) => isMemurUser
            ? schema
                .required("Yol izni zorunludur")
                .test('is-number', 'Yol izni sayısal bir değer olmalıdır', (value) => {
                    if (!value) return false;
                    return !isNaN(Number(value));
                })
            : schema.nullable(),
        otherwise: (schema) => schema.nullable()
    }),
});

const IzinBasvuru = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const editSqId = location.state?.sqId; // Liste sayfasından gelen sqId

    const [isLoad, setIsLoad] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const user = SessionStorageService.getUserInfo();

    // Sayfa yüklenme kontrolü - çift çağrıyı önlemek için
    const isPageLoadedRef = useRef(false);

    // API istek kontrolü - aynı anda birden fazla istek engelleme
    const isHakedisBilgileriLoadingRef = useRef(false);
    const isIzinSuresiBulLoadingRef = useRef(false);

    const [perSicilBilgiKontrol, setPerSicilBilgiKontrol] = useState<any>(null);
    const [memIzinTalepList, setMemIzinTalepList] = useState([]);
    const [editInitialValues, setEditInitialValues] = useState<typeof initialValues | null>(null);
    const [izinTuruList, setIzinTuruList] = useState<any[]>([]);
    const [izinTuruDetayList, setIzinTuruDetayList] = useState<any[]>([]);
    const [ilgiliIzindeKullanilanHakedis, setIlgiliIzindeKullanilanHakedis] = useState<any[]>([]);
    const [hakedisBilgileri, setHakedisBilgileri] = useState<any[]>([]);

    // Yazdır Popup state'leri
    const [yazdirPopupOpen, setYazdirPopupOpen] = useState(false);
    const [selectedIzinFormu, setSelectedIzinFormu] = useState<number>(1);
    const [yazdirLoading, setYazdirLoading] = useState(false);

    // Personel Arama Popup state'leri
    const [personelPopupOpen, setPersonelPopupOpen] = useState(false);
    const [personelPopupTarget, setPersonelPopupTarget] = useState<string>("");
    const [personelSearchLoading, setPersonelSearchLoading] = useState(false);
    const [personelSearchResults, setPersonelSearchResults] = useState<any[]>([]);
    const [personelCurrentPage, setPersonelCurrentPage] = useState(1);
    const [selectedPersonelIndex, setSelectedPersonelIndex] = useState<number | null>(null);
    const personelRecordsPerPage = 5;
    const [personelSearchForm, setPersonelSearchForm] = useState({
        personelTuru: 1,
        adi: "",
        soyadi: "",
        tcKimlikNo: "",
        kurumSicilNo: "",
        calismaDurumu: 1
    });

    const ilgiliHakedisColumns = [
        { header: "Yılı", field: "qtYil", className: "text-center" },
        { header: "İzin Süresi", field: "qtIzinSuresi", className: "text-center" },
    ];

    const hakedisBilgileriColumns = [
        { header: "Hakettiği Tarih", field: "dtHakettigiTarih", className: "text-center" },
        { header: "Hakettiği İzin Yılı", field: "qtYili", className: "text-center" },
        { header: "Hakettiği İzin Süresi", field: "qtHakettigiIzin", className: "text-center" },
        { header: "Kullanılan İzin Süresi", field: "qtKullanilanIzinSuresi", className: "text-center" },
        { header: "Kalan İzin Süresi", field: "kalanIzin", className: "text-center" },
    ];

    // Memur mu İşçi mi kontrolü
    const isMemur = () => perSicilBilgiKontrol?.memurIsci === 'M';
    const isIsci = () => ['I', 'T', 'K'].includes(perSicilBilgiKontrol?.memurIsci);

    // kullanılmıyor, özel tablo yapıldı
    const personelSearchColumns = [
        { header: "TC Kimlik No", field: "tcKimlikNo", className: "text-center" },
        { header: "Kurum Sicil No", field: "qtKurumSicilNo", className: "text-center" },
        { header: "Adı", field: "adiSoyadi", className: "text-center" },
        { header: "Ünvanı", field: "uniteAdi", className: "text-center" },
    ];

    // personel popup
    const openPersonelPopup = (target: string) => {
        setPersonelPopupTarget(target);
        setPersonelPopupOpen(true);
    };

    // personel arama
    const handlePersonelSearch = async () => {
        try {
            setPersonelSearchLoading(true);
            const request = new PerSicilBilgiOzelListRequest(
                undefined,
                undefined,
                personelSearchForm.personelTuru || undefined,
                personelSearchForm.adi || undefined,
                personelSearchForm.soyadi || undefined,
                personelSearchForm.tcKimlikNo ? Number(personelSearchForm.tcKimlikNo) : undefined,
                personelSearchForm.kurumSicilNo ? Number(personelSearchForm.kurumSicilNo) : undefined,
                personelSearchForm.calismaDurumu || undefined
            );
            const response = await MemurIzinTalepAPI.perSicilBilgiOzelList(request);
            if (response?.data) {
                setPersonelSearchResults(response.data);
                setPersonelCurrentPage(1);
                setSelectedPersonelIndex(null);
            }
        } catch (err) {
            console.error('Personel arama hatası:', err);
            toast.error("Personel arama sırasında hata oluştu.");
        } finally {
            setPersonelSearchLoading(false);
        }
    };

    // Timezone-safe tarih dönüşümü - UTC kaymasını önler
    const toSafeDate = (dayjsDate: dayjs.Dayjs): Date => {
        // Tarihi öğlen 12:00'ye ayarlayarak UTC dönüşümünde gün kaymasını önle
        return dayjsDate.hour(12).minute(0).second(0).millisecond(0).toDate();
    };

    // tarih formatlama
    const formatDate = (dateString: any): string => {
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return "";
            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const year = date.getFullYear();
            return `${day}.${month}.${year}`;
        } catch {
            return "";
        }
    };

    // ilgili izinde kullanılan hakediş
    const loadIlgiliIzindeKullanilanHakedis = async (izinTalepSqId: number) => {
        try {
            let response;
            if (isMemur()) {
                // Memur için MEM_IZIN_TALEP_DETAY_LIST
                const request = new MemIzinTalepDetayListRequest(izinTalepSqId);
                response = await MemurIzinTalepAPI.memIzinTalepDetayList(request);
            } else if (isIsci()) {
                // İşçi için ISM_IZIN_TALEP_DETAY_LIST
                const request = new IsmIzinTalepDetayListRequest(izinTalepSqId);
                response = await IsciIzinTalepAPI.ismIzinTalepDetayList(request);
            }
            if (response?.data) {
                setIlgiliIzindeKullanilanHakedis(response.data);
            }
        } catch (err) {
            console.error('İlgili İzinde Kullanılan Hakediş yüklenirken hata:', err);
        }
    };

    // hakediş bilgileri
    const loadHakedisBilgileri = async (rfOzlukBilgi: number, perIzinTuruTanimiDetay: number, dtIslemTarihi: Date) => {
        // Zaten bir istek devam ediyorsa yenisini engelle
        if (isHakedisBilgileriLoadingRef.current) {
            return;
        }

        try {
            isHakedisBilgileriLoadingRef.current = true;
            let response;
            if (isMemur()) {
                // Memur için MEM_IZIN_DURUM_LIST
                const request = new MemIzinDurumListRequest(rfOzlukBilgi, perIzinTuruTanimiDetay, dtIslemTarihi);
                response = await MemurIzinTalepAPI.memIzinDurumList(request);
            } else if (isIsci()) {
                // İşçi için ISM_IZIN_DURUM_LIST
                const request = new IsmIzinDurumListRequest(rfOzlukBilgi, perIzinTuruTanimiDetay, dtIslemTarihi);
                response = await IsciIzinTalepAPI.ismIzinDurumList(request);
            }
            if (response?.data) {
                const formattedData = response.data.map((item: any) => ({
                    ...item,
                    dtHakettigiTarih: formatDate(item.dtHakettigiTarih)
                }));
                setHakedisBilgileri(formattedData);
            }
        } catch (err) {
            console.error('Hakediş Bilgileri yüklenirken hata:', err);
        } finally {
            isHakedisBilgileriLoadingRef.current = false;
        }
    };

    // İzin süresi hesaplama fonksiyonu - MEM_IZIN_SURESI_BUL / ISM_IZIN_SURESI_BUL çağrısı
    const izinSuresiBulVeAyarla = async (
        setFieldValue: any,
        rfOzlukBilgi: number,
        rfPerIzinTuruTnmDetay: number,
        dtIzinAyrilisTarihi: Date,
        dtIzinBitisTarihi: Date,
        qtIzinSuresi: number | null,
        qtYolIzni: number | null
    ) => {
        // Zaten bir istek devam ediyorsa yenisini engelle
        if (isIzinSuresiBulLoadingRef.current) {
            return;
        }

        try {
            isIzinSuresiBulLoadingRef.current = true;
            let response;
            if (isMemur()) {
                // Memur için MEM_IZIN_SURESI_BUL
                const request = new MemIzinSuresiBulRequest(
                    rfOzlukBilgi,
                    undefined,
                    rfPerIzinTuruTnmDetay,
                    dtIzinAyrilisTarihi,
                    dtIzinBitisTarihi,
                    qtIzinSuresi ?? undefined,
                    qtYolIzni ?? undefined
                );
                response = await MemurIzinTalepAPI.memIzinSuresiBul(request);
            } else if (isIsci()) {
                // İşçi için ISM_IZIN_SURESI_BUL
                const request = new IsmIzinSuresiBulRequest(
                    rfOzlukBilgi,
                    rfPerIzinTuruTnmDetay,
                    dtIzinAyrilisTarihi,
                    dtIzinBitisTarihi,
                    qtIzinSuresi ?? undefined
                );
                response = await IsciIzinTalepAPI.ismIzinSuresiBul(request);
            }

            if (response?.data) {
                const result = Array.isArray(response.data) ? response.data[0] : response.data;

                // Kullanılan İzin Süresi - Memur: pQtKullanilanIzinSuresi, İşçi: qtKullanilanIzinSuresi
                const kullanilanIzinSuresi = result.pQtKullanilanIzinSuresi ?? result.qtKullanilanIzinSuresi;
                if (kullanilanIzinSuresi !== undefined) {
                    setFieldValue("kullanilanIzinSuresi", kullanilanIzinSuresi.toString());
                    // Toplam İzin hesapla - qtYolIzni null ise sadece kullanilanIzinSuresi kullan
                    // Yol izni daha sonra onChange'de ayrıca hesaplanacak
                    const yolIzniValue = qtYolIzni !== null ? qtYolIzni : 0;
                    const toplamIzin = Number(kullanilanIzinSuresi) + yolIzniValue;
                    setFieldValue("toplamIzin", toplamIzin.toString());
                }

                // İşe Başlama Tarihi - Memur: pDtIseBaslamaTarihi, İşçi: dtIseBaslamaTarihi
                const iseBaslamaTarihi = result.pDtIseBaslamaTarihi ?? result.dtIseBaslamaTarihi;
                if (iseBaslamaTarihi) {
                    setFieldValue("iseBaslamaTarihi", dayjs(iseBaslamaTarihi));
                }

                // İzin Bitiş Tarihi (Yol İzni değişikliğinde) - Memur: pDtBitisTarihi, İşçi: dtBitisTarihi
                const bitisTarihi = result.pDtBitisTarihi ?? result.dtBitisTarihi;
                if (bitisTarihi && qtYolIzni !== null) {
                    setFieldValue("izinBitisTarihi", dayjs(bitisTarihi));
                }
            }
        } catch (err) {
            console.error('İzin süresi hesaplanırken hata:', err);
        } finally {
            isIzinSuresiBulLoadingRef.current = false;
        }
    };

    // submit
    const handleSave = async (values: typeof initialValues, { setFieldValue }: any) => {
        try {
            setIsSaving(true);

            if (!perSicilBilgiKontrol?.qtSicilId) {
                toast.error("Sicil bilgisi bulunamadı!");
                setIsSaving(false);
                return;
            }

            // Tarih ve saat birleştirme
            // Timezone sorununu önlemek için tarihleri öğlen 12:00'ye ayarlıyoruz
            // Bu sayede UTC dönüşümünde tarih kayması olmaz
            let izinAyrilisTarihi: Date;
            let izinBitisTarihi: Date;

            if (values.isSaatlikIzin && values.izinBaslangicSaat && values.izinBitisSaat) {
                const [startHour, startMin] = values.izinBaslangicSaat.split(':').map(Number);
                const [endHour, endMin] = values.izinBitisSaat.split(':').map(Number);

                izinAyrilisTarihi = values.izinBaslangicTarihi.hour(startHour).minute(startMin).second(0).toDate();
                izinBitisTarihi = values.izinBitisTarihi.hour(endHour).minute(endMin).second(0).toDate();
            } else {
                // Günlük izinlerde saat 12:00 olarak ayarla (timezone shift önleme)
                izinAyrilisTarihi = values.izinBaslangicTarihi.hour(12).minute(0).second(0).toDate();
                izinBitisTarihi = values.izinBitisTarihi.hour(12).minute(0).second(0).toDate();
            }

            // Diğer tarihleri de timezone-safe olarak ayarla
            const izinTalepTarihi = values.izinTalepTarihi.hour(12).minute(0).second(0).toDate();
            const iseBaslamaTarihi = values.iseBaslamaTarihi?.hour(12).minute(0).second(0).toDate();

            // Tarih kontrolü: Başlangıç tarihi bitiş tarihinden büyük olamaz (sadece gün karşılaştırması)
            const baslangicGun = dayjs(izinAyrilisTarihi).startOf('day');
            const bitisGun = dayjs(izinBitisTarihi).startOf('day');
            if (baslangicGun.isAfter(bitisGun)) {
                toast.error("İzin başlangıç tarihi bitiş tarihinden büyük olamaz!");
                setIsSaving(false);
                return;
            }

            // İzin süresi kontrolü: 365 günden fazla izin olamaz
            const gunFarki = Math.floor((izinBitisTarihi.getTime() - izinAyrilisTarihi.getTime()) / (1000 * 60 * 60 * 24));
            if (gunFarki > 365) {
                toast.error("İzin süresi 365 günden fazla olamaz!");
                setIsSaving(false);
                return;
            }

            // Saatlik izinde kullanılan izin süresi 0 olmalı
            let kullanilanIzinSuresiValue = 0;
            if (!values.isSaatlikIzin && values.kullanilanIzinSuresi) {
                kullanilanIzinSuresiValue = Math.round(Number(values.kullanilanIzinSuresi));
            }

            // DEBUG: Vekalet Eden kontrol
            console.log("🔍 SAVE - vekaletEdenId:", values.vekaletEdenId);
            console.log("🔍 SAVE - vekaletEden:", values.vekaletEden);

            let response;
            if (isMemur()) {
                // Memur için MEM_IZIN_TALEP_SAVE
                const request = new MemIzinTalepSaveRequest(
                    values.sqId || undefined,
                    perSicilBilgiKontrol.qtSicilId,
                    values.izinTuruDetayId || undefined,
                    izinTalepTarihi,
                    izinAyrilisTarihi,
                    izinBitisTarihi,
                    kullanilanIzinSuresiValue,
                    values.yolIzni ? Number(values.yolIzni) : 0,
                    undefined, // rfOrtIletisimMaster
                    values.izinAdresi || "",
                    iseBaslamaTarihi,
                    values.izinAciklama || "",
                    values.izinDurumu,
                    new Date(), // dtDurumTarih
                    values.redAciklama || "",
                    user?.kullaniciKodu || "",
                    undefined, // rfMemIzinHakedisBilgi
                    values.vekaletEdenId || undefined,
                    values.onayaSunanId || undefined,
                    values.onayaSunanGorevId || undefined,
                    values.uygunGorenId || undefined,
                    values.uygunGorenGorevId || undefined,
                    values.onaylayanId || undefined,
                    values.onaylayanGorevId || undefined
                );
                response = await MemurIzinTalepAPI.memIzinTalepSave(request);
            } else if (isIsci()) {
                // İşçi için ISM_IZIN_TALEP_SAVE
                // Saatlik izinde yol izni gönderilmemeli (NULL olarak gitmeli)
                const yolIzniValue = values.isSaatlikIzin ? undefined : (values.yolIzni ? Number(values.yolIzni) : 0);
                const request = new IsmIzinTalepSaveRequest(
                    values.sqId || undefined,
                    perSicilBilgiKontrol.qtSicilId,
                    values.izinTuruDetayId || undefined,
                    undefined, // rfOrtIletisimMaster
                    undefined, // rfIscIzinHakedisBilgi
                    izinTalepTarihi,
                    izinAyrilisTarihi,
                    izinBitisTarihi,
                    kullanilanIzinSuresiValue,
                    iseBaslamaTarihi,
                    undefined, // qtIzinBelgeNo
                    values.izinAdresi || "",
                    yolIzniValue,
                    values.izinAciklama || "",
                    undefined, // qtIzinSaat
                    user?.kullaniciKodu || "",
                    undefined, // dtHastaneGirisSaati
                    undefined, // dtHastaneCikisSaati
                    undefined, // qtHastaneYolSure
                    values.vekaletEdenId || undefined,
                    values.onayaSunanId || undefined,
                    values.onayaSunanGorevId || undefined,
                    values.uygunGorenId || undefined,
                    values.uygunGorenGorevId || undefined,
                    values.onaylayanId || undefined,
                    values.onaylayanGorevId || undefined,
                    values.izinDurumu,
                    new Date(), // dtDurumTarih
                    values.redAciklama || ""
                );
                response = await IsciIzinTalepAPI.ismIzinTalepSave(request);
            }

            if (response?.data) {
                const result = Array.isArray(response.data) ? response.data[0] : response.data;

                console.log("save response", response)
                const sonuc = result.sonuc;
                const sonucAciklama = result.sonucAciklama;
                const uyari = result.uyari;
                const sqId = result.sqId;
                const chDurumAciklama = result.chDurumAciklama;

                if (sonuc === 0) {
                    // Analiz'e göre SONUC_ACIKLAMA gösterilecek
                    toast.success(sonucAciklama || uyari || "Kayıt başarıyla tamamlandı.");
                    if (sqId) {
                        setFieldValue("sqId", sqId);
                        // Red açıklaması (CH_DURUM_ACIKLAMA) varsa göster
                        if (chDurumAciklama) {
                            setFieldValue("redAciklama", chDurumAciklama);
                        }
                        // Save işleminden sonra MEM_IZIN_TALEP_DETAY_LIST çağrılacak 
                        await loadIlgiliIzindeKullanilanHakedis(sqId);
                        // Hakediş bilgilerini de güncelle 
                        if (values.izinTuruDetayId) {
                            await loadHakedisBilgileri(
                                perSicilBilgiKontrol.qtSicilId,
                                values.izinTuruDetayId,
                                izinAyrilisTarihi
                            );
                        }
                    } else {
                        console.log('sqId bulunamadı! result:', result);
                    }
                } else {
                    // Analiz'e göre SONUC_ACIKLAMA gösterilecek
                    toast.error(sonucAciklama || "Kayıt sırasında hata oluştu.");
                }
            } else {
                toast.error("Sunucudan yanıt alınamadı.");
            }
        } catch (err: any) {
            toast.error('Kayıt hatası:', err);
        } finally {
            setIsSaving(false);
        }
    };

    useEffect(() => {
        // StrictMode'da çift çağrıyı önle - CRITICAL: Rate limiting hatalarını önlemek için
        if (isPageLoadedRef.current) return;
        isPageLoadedRef.current = true;

        const sayfaYukle = async () => {
            try {
                setIsLoad(true);

                const getCtMemurIsci = (personelTipi: any) => {
                    switch (personelTipi) {
                        case 'M': return 1;
                        case 'I': return 2;
                        case 'T': return 3;
                        case 'S': return 4;
                        default: return 1;
                    }
                };

                const perSicilBilgiKontrolRequest = { vatandaslikNo: user.tcKimlikNo };
                const perSicilBilgiKontrolResponse = await MemurIzinTalepAPI.perSicilBilgiKontrol(perSicilBilgiKontrolRequest);

                if (perSicilBilgiKontrolResponse.data.sonuc !== 0) {
                    toast.error(perSicilBilgiKontrolResponse.data.sonucAciklama);
                    return;
                }

                setPerSicilBilgiKontrol(perSicilBilgiKontrolResponse.data);

                const memurIsci = perSicilBilgiKontrolResponse.data.memurIsci;
                const sicilId = perSicilBilgiKontrolResponse.data.qtSicilId;
                const isMemurUser = memurIsci === 'M';
                const isIsciUser = ['I', 'T', 'K'].includes(memurIsci);

                // Memur veya İşçi/Taşeron/Sözleşmeli ise izin listesini yükle
                if (isMemurUser || isIsciUser) {
                    let izinTalepResponse;
                    if (isMemurUser) {
                        const izinTalepRequest = new MemIzinTalepListRequest(memurIsci, sicilId);
                        izinTalepResponse = await MemurIzinTalepAPI.memIzinTalepList(izinTalepRequest);
                    } else {
                        const izinTalepRequest = new IsmIzinTalepListRequest(memurIsci, sicilId);
                        izinTalepResponse = await IsciIzinTalepAPI.ismIzinTalepList(izinTalepRequest);
                    }
                    if (izinTalepResponse?.data) {
                        setMemIzinTalepList(izinTalepResponse.data);
                    }
                }

                // izin türü - memurIsci değerine göre personelDurum belirlenir
                // Backend personelDurum'u yanlış dönebiliyor, memurIsci'den hesapla
                const personelDurum = getCtMemurIsci(memurIsci);
                const izinTuruRequest = new PerIzinTuruTanimiSaatListRequest(personelDurum);
                const izinTuruResponse = await MemurIzinTalepAPI.perIzinTuruTanimiSaatList(izinTuruRequest);
                if (izinTuruResponse?.data) {
                    setIzinTuruList(izinTuruResponse.data);
                }

                // Edit modu: sqId varsa kaydı getir
                if (editSqId) {
                    let getResponse;
                    if (isMemurUser) {
                        const getRequest = new MemIzinTalepGetRequest(editSqId);
                        getResponse = await MemurIzinTalepAPI.memIzinTalepGet(getRequest);
                    } else if (isIsciUser) {
                        const getRequest = new IsmIzinTalepGetRequest(editSqId);
                        getResponse = await IsciIzinTalepAPI.ismIzinTalepGet(getRequest);
                    }

                    if (getResponse?.data) {
                        const data = Array.isArray(getResponse.data) ? getResponse.data[0] : getResponse.data;
                        console.log("🔍 GET RESPONSE DATA:", data);

                        // İşçi ve Memur için farklı field adları olabilir
                        const izinTuruTanimiId = data.rfPerIzinTuruTanimi;
                        const izinTuruDetayTanimiId = data.rfPerIzinTuruDetayTanimi || data.rfPerIzinTuruTanimiDetay;
                        const baslangicTarihi = data.dtIzinAyrilisTarihi || data.dtBaslangicTarihi;
                        const bitisTarihi = data.dtIzinBitisTarihi || data.dtBitisTarihi;
                        const talepTarihi = data.dtIzinTalepTarihi || data.dtTalepTarihi;
                        const iseBaslamaTarihiData = data.dtIseBaslamaTarihi || data.dtGoreveBaslamaTarihi;
                        const izinSuresi = data.qtKullanilanIzinSuresi || data.qtIzinSuresi || "";
                        const izinAdresiData = data.chIzinAdresi || data.chIzinAdres || "";

                        // İzin türü detay listesini yükle
                        if (izinTuruTanimiId) {
                            const detayRequest = new PerIzinTuruTnmDtySaatLstRequest(izinTuruTanimiId);
                            const detayResponse = await MemurIzinTalepAPI.perIzinTuruTnmDtySaatLst(detayRequest);
                            if (detayResponse?.data) {
                                setIzinTuruDetayList(detayResponse.data);
                            }
                        }

                        // İzin türü detay bilgilerini al (saatlik/yıllık kontrol için)
                        let isSaatlik = false;
                        let isYillik = false;
                        if (izinTuruDetayTanimiId) {
                            const detayGetRequest = new PerIzinTuruTanimiDetayGetRequest(izinTuruDetayTanimiId);
                            const detayGetResponse = await MemurIzinTalepAPI.perIzinTuruTanimiDetayGet(detayGetRequest);
                            if (detayGetResponse?.data) {
                                isSaatlik = detayGetResponse.data.ctSaat === 1;
                                isYillik = detayGetResponse.data.ctTuru === 1;
                            }
                        }

                        // Form değerlerini set et
                        const editValues: typeof initialValues = {
                            sqId: data.sqId,
                            izinTuru: "",
                            izinTuruId: izinTuruTanimiId || null,
                            izinTuruDetay: "",
                            izinTuruDetayId: izinTuruDetayTanimiId || null,
                            izinTalepTarihi: talepTarihi ? dayjs(talepTarihi) : dayjs(),
                            izinBaslangicTarihi: baslangicTarihi ? dayjs(baslangicTarihi) : dayjs(),
                            izinBitisTarihi: bitisTarihi ? dayjs(bitisTarihi) : dayjs(),
                            izinBaslangicSaat: isSaatlik && baslangicTarihi ? dayjs(baslangicTarihi).format("HH:mm") : null,
                            izinBitisSaat: isSaatlik && bitisTarihi ? dayjs(bitisTarihi).format("HH:mm") : null,
                            kullanilanIzinSuresi: izinSuresi,
                            yolIzni: data.qtYolIzni?.toString() || "",
                            toplamIzin: ((Number(izinSuresi) || 0) + (data.qtYolIzni || 0)).toString(),
                            iseBaslamaTarihi: iseBaslamaTarihiData ? dayjs(iseBaslamaTarihiData) : dayjs().add(1, 'day'),
                            izinDurumu: data.ctDurum ?? 0,
                            izinAciklama: data.chAciklama || "",
                            izinAdresi: izinAdresiData,
                            redAciklama: data.chDurumAciklama || "",
                            vekaletEdenId: data.rfPerSicilBilgiVekil || null,
                            vekaletEden: data.rfPerVekilChAdiSoyadi || "",
                            vekaletEdenUnvan: data.chPersonelDurumVekil || "",
                            onayaSunanId: data.rfPerSicilBilgiOnayaSun || null,
                            onayaSunan: data.rfPerOnySunChAdiSoyadi || "",
                            onayaSunanGorevId: data.rfPerSicGorevOnayaSun || null,
                            onayaSunanUnvan: data.rfLpGorevAciklamaOnayaSun || "",
                            uygunGorenId: data.rfPerSicilBilgiUygunG || null,
                            uygunGoren: data.rfPerUygunGChAdiSoyadi || "",
                            uygunGorenGorevId: data.rfPerSicGorevUygunG || null,
                            uygunGorenUnvan: data.rfLpGorevAciklamaUygunG || "",
                            onaylayanId: data.rfPerSicilBilgiOnay || null,
                            onaylayan: data.rfPerOnayChAdiSoyadi || "",
                            onaylayanGorevId: data.rfPerSicGorevOnay || null,
                            onaylayanUnvan: data.rfLpGorevAciklamaOnay || "",
                            isSaatlikIzin: isSaatlik,
                            isYillikIzin: isYillik
                        };

                        setEditInitialValues(editValues);

                        // İlgili İzinde Kullanılan Hakediş tablosunu yükle (await ile sıralı)
                        await loadIlgiliIzindeKullanilanHakedis(editSqId);

                        // Hakediş Bilgileri tablosunu yükle (await ile sıralı)
                        if (izinTuruDetayTanimiId && sicilId) {
                            await loadHakedisBilgileri(
                                sicilId,
                                izinTuruDetayTanimiId,
                                baslangicTarihi ? new Date(baslangicTarihi) : new Date()
                            );
                        }
                    }
                }
            } catch (err) {
                console.error('Sayfa yüklenirken hata:', err);
            } finally {
                setIsLoad(false);
            }
        };

        sayfaYukle();
    }, []);

    if (isLoad) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
                <CircularProgress />
            </div>
        );
    }

    // Edit modunda veri yüklenene kadar bekle
    if (editSqId && !editInitialValues) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
                <CircularProgress />
            </div>
        );
    }

    return (
        <>
            <Formik
                initialValues={editInitialValues || initialValues}
                validationSchema={getValidationSchema(isMemur())}
                onSubmit={handleSave}
                validateOnMount={false}
                enableReinitialize={true}
            >
                {(formikProps) => {
                    const { values, errors, touched, setFieldValue, handleChange, resetForm, setFieldTouched } = formikProps;
                    const isSaatlikIzin = values.isSaatlikIzin;
                    const isYillikIzin = values.isYillikIzin;
                    const isFormDisabled = !values.izinTuruId || !values.izinTuruDetayId;

                    // Kayıt yapılmış mı kontrolü (sqId varsa kayıt yapılmış)
                    const isKayitYapilmis = !!values.sqId;
                    // Onay alanları sadece kayıt yapıldıktan sonra aktif
                    const isOnayAlanlariDisabled = !isKayitYapilmis;
                    // Onaya gönder butonu: kayıt yapılmış ve durum "İşlemde" (0) ise aktif
                    const isOnayaGonderDisabled = !isKayitYapilmis || values.izinDurumu !== 0;

                    const hasError = (field: keyof typeof initialValues) => touched[field] && errors[field];
                    const getErrorText = (field: keyof typeof initialValues) => touched[field] && errors[field] ? String(errors[field]) : "";

                    const computeIseBaslamaTarihi = (bitisTarihi: dayjs.Dayjs) => bitisTarihi.add(1, 'day');

                    // Tarih validasyonu - API çağrısından önce kontrol
                    const validateTarihler = (baslangicTarihi: Date, bitisTarihi: Date): boolean => {
                        // Başlangıç tarihi bitiş tarihinden büyük olamaz (sadece gün karşılaştırması)
                        const baslangicGun = dayjs(baslangicTarihi).startOf('day');
                        const bitisGun = dayjs(bitisTarihi).startOf('day');
                        if (baslangicGun.isAfter(bitisGun)) {
                            toast.error("İzin başlangıç tarihi bitiş tarihinden büyük olamaz!");
                            return false;
                        }
                        // 365 günden fazla izin olamaz
                        const gunFarki = Math.floor((bitisTarihi.getTime() - baslangicTarihi.getTime()) / (1000 * 60 * 60 * 24));
                        if (gunFarki > 365) {
                            toast.error("İzin süresi 365 günden fazla olamaz!");
                            return false;
                        }
                        return true;
                    };

                    const handleOnayaGonder = async () => {
                        if (!values.sqId) {
                            toast.error("Önce kaydı tamamlamanız gerekmektedir!");
                            return;
                        }

                        try {
                            setIsSaving(true);
                            let response;

                            if (isMemur()) {
                                // Memur için MEM_IZIN_TALEP_DURUM_UPD
                                const request = new MemIzinTalepDurumUpdRequest(
                                    values.sqId,
                                    user?.kullaniciKodu || ""
                                );
                                response = await MemurIzinTalepAPI.memIzinTalepDurumUpd(request);
                            } else if (isIsci()) {
                                // İşçi için ISM_IZIN_TALEP_DURUM_UPD
                                const request = new IsmIzinTalepDurumUpdRequest(
                                    values.sqId,
                                    user?.kullaniciKodu || ""
                                );
                                response = await IsciIzinTalepAPI.ismIzinTalepDurumUpd(request);
                            }

                            if (response?.data) {
                                const result = Array.isArray(response.data) ? response.data[0] : response.data;
                                const sonuc = result.sonuc;
                                const sonucAciklama = result.sonucAciklama;

                                if (sonuc === 0) {
                                    toast.success(sonucAciklama || "İzin talebi onaya gönderildi.");
                                    // İzin Talep İşlem sayfasına yönlendir
                                    navigate("/personel-islemleri/izin-talep-islem");
                                } else {
                                    toast.error(sonucAciklama || "Onaya gönderme sırasında hata oluştu.");
                                }
                            }
                        } catch (err: any) {
                            toast.error('Onaya gönderme hatası:', err);
                        } finally {
                            setIsSaving(false);
                        }
                    };

                    // Yazdır butonuna tıklandığında
                    const handleYazdirClick = () => {
                        // CT_DURUM 2 veya 3 ise rapor alınamaz
                        if (values.izinDurumu === 2 || values.izinDurumu === 3) {
                            toast.error("Onaylanmış İzinlerle İlgili Hiçbir İşlem Yapılamaz. İnsan Kaynakları İle İletişime Geçiniz.");
                            return;
                        }
                        // İşçi için default değer 4 (İşçi Yıllık İzin Formu)
                        setSelectedIzinFormu(isMemur() ? 1 : 4);
                        setYazdirPopupOpen(true);
                    };

                    // Yazdır popup'ında Yazdır butonuna tıklandığında
                    const handleYazdirConfirm = async () => {
                        if (!values.sqId) {
                            toast.error("Kayıt bulunamadı!");
                            return;
                        }

                        try {
                            setYazdirLoading(true);
                            console.log("📄 RAPOR PARAMETRELERİ:", {
                                sqId: values.sqId,
                                izinForm: selectedIzinFormu,
                                isMemur: isMemur(),
                                isIsci: isIsci(),
                                vekaletEden: values.vekaletEden,
                                vekaletEdenUnvan: values.vekaletEdenUnvan,
                                onayaSunan: values.onayaSunan,
                                onayaSunanUnvan: values.onayaSunanUnvan,
                                uygunGoren: values.uygunGoren,
                                uygunGorenUnvan: values.uygunGorenUnvan,
                                onaylayan: values.onaylayan,
                                onaylayanUnvan: values.onaylayanUnvan
                            });
                            const response = await IzinTalepRaporAPI.getExportPdf(
                                values.sqId,
                                selectedIzinFormu,
                                values.vekaletEden || "",
                                values.vekaletEdenUnvan || "",
                                values.onayaSunan || "",
                                values.onayaSunanUnvan || "",
                                values.uygunGoren || "",
                                values.uygunGorenUnvan || "",
                                values.onaylayan || "",
                                values.onaylayanUnvan || ""
                            );

                            if (response?.data?.reportUrlByteFormat) {
                                openFileInNewTab(
                                    response.data.reportUrlByteFormat,
                                    "IzinFormu.pdf"
                                );
                                setYazdirPopupOpen(false);
                            } else {
                                toast.error("Rapor oluşturulurken bir hata oluştu.");
                            }
                        } catch (err) {
                            console.error("Rapor hatası:", err);
                            toast.error("Rapor oluşturulurken bir hata oluştu.");
                        } finally {
                            setYazdirLoading(false);
                        }
                    };

                    const handleIzinBaslangicChange = async (val: any) => {
                        setFieldValue("izinBaslangicTarihi", val);
                        if (values.isSaatlikIzin) {
                            setFieldValue("izinBitisTarihi", val);
                            setFieldValue("iseBaslamaTarihi", computeIseBaslamaTarihi(val));
                            return;
                        }
                        setFieldValue("iseBaslamaTarihi", computeIseBaslamaTarihi(values.izinBitisTarihi));

                        // CT_SAAT <> 1 ve İzin Türü Detay seçili ise MEM_IZIN_SURESI_BUL çağır
                        if (!values.isSaatlikIzin && values.izinTuruDetayId && perSicilBilgiKontrol?.qtSicilId) {
                            // Tarih validasyonu - API çağrısından önce kontrol
                            const safeBaslangic = toSafeDate(val);
                            const safeBitis = toSafeDate(values.izinBitisTarihi);
                            if (!validateTarihler(safeBaslangic, safeBitis)) {
                                return;
                            }
                            // DÜZELTME: İzin başlangıç tarihi değiştiğinde yol izni null olmalı (dokümantasyon gereği)
                            await izinSuresiBulVeAyarla(
                                setFieldValue,
                                perSicilBilgiKontrol.qtSicilId,
                                values.izinTuruDetayId,
                                safeBaslangic,
                                safeBitis,
                                0, // qtIzinSuresi
                                null // qtYolIzni - dokümana göre null
                            );
                        }
                    };

                    const handleIzinBitisChange = async (val: any) => {
                        setFieldValue("izinBitisTarihi", val);
                        setFieldValue("iseBaslamaTarihi", computeIseBaslamaTarihi(val));

                        // CT_SAAT <> 1 ve İzin Türü Detay seçili ise MEM_IZIN_SURESI_BUL çağır
                        if (!values.isSaatlikIzin && values.izinTuruDetayId && perSicilBilgiKontrol?.qtSicilId) {
                            // Tarih validasyonu - API çağrısından önce kontrol
                            const safeBaslangic = toSafeDate(values.izinBaslangicTarihi);
                            const safeBitis = toSafeDate(val);
                            if (!validateTarihler(safeBaslangic, safeBitis)) {
                                return;
                            }
                            // DÜZELTME: İzin bitiş tarihi değiştiğinde yol izni null olmalı (dokümantasyon gereği)
                            await izinSuresiBulVeAyarla(
                                setFieldValue,
                                perSicilBilgiKontrol.qtSicilId,
                                values.izinTuruDetayId,
                                safeBaslangic,
                                safeBitis,
                                0, // qtIzinSuresi
                                null // qtYolIzni - dokümana göre null
                            );
                        }
                    };

                    const handleKullanilanIzinSuresiChange = async (e: any) => {
                        const kullanilanIzinSuresiVal = e.target.value;
                        setFieldValue("kullanilanIzinSuresi", kullanilanIzinSuresiVal);

                        // Toplam İzin hesapla
                        const yolIzni = Number(values.yolIzni) || 0;
                        const kullanilanIzin = Number(kullanilanIzinSuresiVal) || 0;
                        setFieldValue("toplamIzin", (kullanilanIzin + yolIzni).toString());

                        // CT_SAAT <> 1 ve İzin Türü Detay seçili ise API çağır
                        if (!values.isSaatlikIzin && values.izinTuruDetayId && perSicilBilgiKontrol?.qtSicilId && kullanilanIzinSuresiVal) {
                            // Tarih validasyonu - API çağrısından önce kontrol
                            const safeBitis = toSafeDate(values.izinBitisTarihi);

                            // Mevcut yol izni değerini kullan
                            const mevcutYolIzni = values.yolIzni ? Number(values.yolIzni) : 0;

                            // API çağrısı - İşe Başlama ve Bitiş Tarihi için
                            try {
                                if (isIzinSuresiBulLoadingRef.current) {
                                    return;
                                }

                                isIzinSuresiBulLoadingRef.current = true;
                                let response;

                                if (isMemur()) {
                                    // DÜZELTME: Kullanılan izin süresi değiştiğinde başlangıç tarihi bitiş tarihiyle aynı
                                    // Dokümantasyon: v_DT_IZIN_AYRILIS_TARIHI > null diyor ama API null kabul etmiyor
                                    // Backend'in davranışına göre bitiş tarihi gönderiliyor
                                    const request = new MemIzinSuresiBulRequest(
                                        perSicilBilgiKontrol.qtSicilId,
                                        undefined,
                                        values.izinTuruDetayId,
                                        safeBitis, // Başlangıç tarihi = Bitiş tarihi (dokümana göre null ama API kabul etmiyor)
                                        safeBitis,
                                        Number(kullanilanIzinSuresiVal) || 0,
                                        mevcutYolIzni
                                    );
                                    response = await MemurIzinTalepAPI.memIzinSuresiBul(request);
                                } else if (isIsci()) {
                                    const request = new IsmIzinSuresiBulRequest(
                                        perSicilBilgiKontrol.qtSicilId,
                                        values.izinTuruDetayId,
                                        safeBitis, // Başlangıç tarihi = Bitiş tarihi
                                        safeBitis,
                                        Number(kullanilanIzinSuresiVal) || 0
                                    );
                                    response = await IsciIzinTalepAPI.ismIzinSuresiBul(request);
                                }

                                if (response?.data) {
                                    const result = Array.isArray(response.data) ? response.data[0] : response.data;

                                    // İşe Başlama Tarihi'ni güncelle
                                    const iseBaslamaTarihi = result.pDtIseBaslamaTarihi ?? result.dtIseBaslamaTarihi;
                                    if (iseBaslamaTarihi) {
                                        setFieldValue("iseBaslamaTarihi", dayjs(iseBaslamaTarihi));
                                    }

                                    // İzin Bitiş Tarihi'ni güncelle
                                    const bitisTarihi = result.pDtBitisTarihi ?? result.dtBitisTarihi;
                                    if (bitisTarihi) {
                                        setFieldValue("izinBitisTarihi", dayjs(bitisTarihi));
                                    }
                                }
                            } catch (err) {
                                console.error('İzin süresi hesaplanırken hata:', err);
                            } finally {
                                isIzinSuresiBulLoadingRef.current = false;
                            }
                        }
                    };

                    const handleIzinTuruChange = async (event: any) => {
                        const selectedValue = event.target.value;
                        const selectedItem = izinTuruList.find(item => item.sqId === selectedValue);
                        setFieldValue("izinTuruId", selectedValue, false);
                        setFieldValue("izinTuru", selectedItem?.chAdi || "", false);
                        setFieldValue("izinTuruDetay", "", false);
                        setFieldValue("izinTuruDetayId", null, false);
                        setFieldValue("isSaatlikIzin", false, false);
                        setFieldValue("isYillikIzin", false, false);
                        // Touched durumunu sıfırla - hata mesajı göstermesin
                        setFieldTouched("izinTuruDetayId", false, false);

                        if (selectedValue) {
                            const detayRequest = new PerIzinTuruTnmDtySaatLstRequest(selectedValue);
                            const detayResponse = await MemurIzinTalepAPI.perIzinTuruTnmDtySaatLst(detayRequest);
                            if (detayResponse?.data) {
                                setIzinTuruDetayList(detayResponse.data);
                            }
                        } else {
                            setIzinTuruDetayList([]);
                        }
                    };

                    const handleIzinTuruDetayChange = async (event: any) => {
                        const selectedValue = event.target.value;
                        const selectedItem = izinTuruDetayList.find(item => item.sqId === selectedValue);

                        if (!selectedItem) return;

                        const detayGetRequest = new PerIzinTuruTanimiDetayGetRequest(selectedItem.sqId);
                        const detayGetResponse = await MemurIzinTalepAPI.perIzinTuruTanimiDetayGet(detayGetRequest);

                        setFieldValue("izinTuruDetayId", selectedValue, false);
                        setFieldValue("izinTuruDetay", selectedItem?.chAdi || "", false);

                        const saatlikMi = detayGetResponse?.data?.ctSaat === 1;
                        setFieldValue("isSaatlikIzin", saatlikMi);

                        const yillikMi = detayGetResponse?.data?.ctTuru === 1;
                        setFieldValue("isYillikIzin", yillikMi);

                        // CT_TURU ≠ 1 ise Yol İzni ve Toplam İzin 0 ve disable
                        if (!yillikMi) {
                            setFieldValue("yolIzni", "0");
                            setFieldValue("toplamIzin", "0");
                        }

                        if (saatlikMi) {
                            setFieldValue("izinBitisTarihi", values.izinBaslangicTarihi);
                            setFieldValue("kullanilanIzinSuresi", "");
                            // Saatlik izinde hakediş bilgileri temizle
                            setHakedisBilgileri([]);
                        } else {
                            // CT_SAAT <> 1 ise Hakediş bilgilerini yükle ve izin süresi hesapla
                            if (perSicilBilgiKontrol?.qtSicilId && selectedValue) {
                                const safeBaslangic = toSafeDate(values.izinBaslangicTarihi);
                                const safeBitis = toSafeDate(values.izinBitisTarihi);

                                await loadHakedisBilgileri(perSicilBilgiKontrol.qtSicilId, selectedValue, safeBaslangic);

                                // Tarih validasyonu - API çağrısından önce kontrol
                                if (!validateTarihler(safeBaslangic, safeBitis)) {
                                    return;
                                }

                                // DÜZELTME: İzin türü detay ilk seçiminde yol izni 0 olmalı (henüz girilmemiş)
                                // MEM_IZIN_SURESI_BUL çağır
                                await izinSuresiBulVeAyarla(
                                    setFieldValue,
                                    perSicilBilgiKontrol.qtSicilId,
                                    selectedValue,
                                    safeBaslangic,
                                    safeBitis,
                                    0, // qtIzinSuresi - Kullanılan İzin Süresi
                                    0  // qtYolIzni - İlk seçimde 0 (henüz girilmemiş)
                                );
                            }
                        }
                    };

                    return (
                        <Form>
                            <div className="row justify-content-center">
                                <div className="col-lg-10 col-12 mb-3 d-flex justify-content-between align-items-center">
                                    <h5 className="mb-0">İzin Talep</h5>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="success"
                                        disabled={isFormDisabled || isSaving}
                                        startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : null}
                                    >
                                        {isSaving ? (isKayitYapilmis ? "Güncelleniyor..." : "Kaydediliyor...") : (isKayitYapilmis ? "Güncelle" : "Kaydet")}
                                    </Button>
                                </div>

                                {/* Sol Kolon */}
                                <div className="col-lg-5 col-md-6 mb-3">
                                    <div className="card h-100">
                                        <div className="card-body p-3">
                                            <TextField fullWidth select label="İzin Türü *" size="small" name="izinTuruId"
                                                value={values.izinTuruId || ""} onChange={handleIzinTuruChange}
                                                error={!!hasError("izinTuruId")} helperText={getErrorText("izinTuruId")} className="mb-3">
                                                <MenuItem value="">Seçiniz</MenuItem>
                                                {izinTuruList.map((item) => <MenuItem key={item.sqId} value={item.sqId}>{item.chAdi}</MenuItem>)}
                                            </TextField>

                                            <TextField fullWidth select label="İzin Türü Detay *" size="small" name="izinTuruDetayId"
                                                value={values.izinTuruDetayId || ""} onChange={handleIzinTuruDetayChange}
                                                disabled={!values.izinTuruId} error={!!hasError("izinTuruDetayId")} helperText={getErrorText("izinTuruDetayId")} className="mb-3">
                                                <MenuItem value="">Seçiniz</MenuItem>
                                                {izinTuruDetayList.map((item) => <MenuItem key={item.sqId} value={item.sqId}>{item.chAdi}</MenuItem>)}
                                            </TextField>

                                            <DatePicker label="İzin Talep Tarihi *" value={values.izinTalepTarihi}
                                                onChange={(val) => setFieldValue("izinTalepTarihi", val)} disabled
                                                slotProps={{ textField: { size: "small", fullWidth: true, className: "mb-3", InputProps: { readOnly: true } } }} />

                                            <div className="row mb-3">
                                                <div className="col-md-6">
                                                    <DatePicker label="İzin Başlangıç Tarihi *" value={values.izinBaslangicTarihi}
                                                        onChange={handleIzinBaslangicChange} disabled={isFormDisabled}
                                                        slotProps={{ textField: { size: "small", fullWidth: true, error: !!hasError("izinBaslangicTarihi") } }} />
                                                </div>
                                                <div className="col-md-6">
                                                    <TimePicker label={isSaatlikIzin ? "Saat *" : "Saat"}
                                                        value={values.izinBaslangicSaat ? dayjs(values.izinBaslangicSaat, "HH:mm") : null}
                                                        onChange={(val) => {
                                                            const formattedVal = val ? dayjs(val).format("HH:mm") : null;
                                                            setFieldValue("izinBaslangicSaat", formattedVal);
                                                        }}
                                                        disabled={isFormDisabled || !isSaatlikIzin}
                                                        slotProps={{ textField: { size: "small", fullWidth: true, InputProps: { readOnly: !isSaatlikIzin }, error: !!hasError("izinBaslangicSaat"), helperText: getErrorText("izinBaslangicSaat") } }} />
                                                </div>
                                            </div>

                                            <div className="row mb-3">
                                                <div className="col-md-6">
                                                    <DatePicker label="İzin Bitiş Tarihi *" value={values.izinBitisTarihi}
                                                        onChange={handleIzinBitisChange} disabled={isFormDisabled || isSaatlikIzin}
                                                        slotProps={{ textField: { size: "small", fullWidth: true, InputProps: { readOnly: isSaatlikIzin }, error: !!hasError("izinBitisTarihi") } }} />
                                                </div>
                                                <div className="col-md-6">
                                                    <TimePicker label={isSaatlikIzin ? "Saat *" : "Saat"}
                                                        value={values.izinBitisSaat ? dayjs(values.izinBitisSaat, "HH:mm") : null}
                                                        onChange={(val) => {
                                                            const formattedVal = val ? dayjs(val).format("HH:mm") : null;
                                                            setFieldValue("izinBitisSaat", formattedVal);
                                                        }}
                                                        disabled={isFormDisabled || !isSaatlikIzin}
                                                        slotProps={{ textField: { size: "small", fullWidth: true, InputProps: { readOnly: !isSaatlikIzin }, error: !!hasError("izinBitisSaat"), helperText: getErrorText("izinBitisSaat") } }} />
                                                </div>
                                            </div>

                                            <TextField
                                                fullWidth
                                                label="Kullanılan İzin Süresi"
                                                size="small"
                                                name="kullanilanIzinSuresi"
                                                value={values.kullanilanIzinSuresi}
                                                type="number"
                                                onChange={handleKullanilanIzinSuresiChange}
                                                disabled={isFormDisabled || isSaatlikIzin}
                                                className="mb-3"
                                            />

                                            <div className="row mb-3">
                                                {/* Yol İzni - Sadece Memur için göster */}
                                                {isMemur() && (
                                                    <div className="col-md-6">
                                                        <TextField fullWidth label={isYillikIzin ? "Yol İzni *" : "Yol İzni"} size="small" name="yolIzni" value={values.yolIzni}
                                                            type="number"
                                                            disabled={isFormDisabled || !isYillikIzin}
                                                            error={isYillikIzin && !!hasError("yolIzni")}
                                                            helperText={isYillikIzin ? getErrorText("yolIzni") : ""}
                                                            onChange={async (e) => {
                                                                const yolIzniVal = e.target.value;
                                                                setFieldValue("yolIzni", yolIzniVal);

                                                                
                                                                // Toplam İzin hesapla
                                                                const kullanilanIzin = Number(values.kullanilanIzinSuresi) || 0;
                                                                const yolIzni = Number(yolIzniVal) || 0;
                                                                setFieldValue("toplamIzin", (kullanilanIzin + yolIzni).toString());

                                                                // CT_SAAT <> 1 ve İzin Türü Detay seçili ise MEM_IZIN_SURESI_BUL çağır
                                                                if (!values.isSaatlikIzin && values.izinTuruDetayId && perSicilBilgiKontrol?.qtSicilId && yolIzniVal) {
                                                                    // Tarih validasyonu - API çağrısından önce kontrol
                                                                    const safeBaslangic = toSafeDate(values.izinBaslangicTarihi);
                                                                    const safeBitis = toSafeDate(values.izinBitisTarihi);
                                                                    if (!validateTarihler(safeBaslangic, safeBitis)) {
                                                                        return;
                                                                    }
                                                                    await izinSuresiBulVeAyarla(
                                                                        setFieldValue,
                                                                        perSicilBilgiKontrol.qtSicilId,
                                                                        values.izinTuruDetayId,
                                                                        safeBaslangic,
                                                                        safeBitis,
                                                                        null, // qtIzinSuresi
                                                                        Number(yolIzniVal) || 0
                                                                    );
                                                                }
                                                            }} />
                                                    </div>
                                                )}
                                                <div className={isMemur() ? "col-md-6" : "col-md-12"}>
                                                    <TextField fullWidth label="Toplam İzin" size="small" name="toplamIzin" value={values.toplamIzin} disabled />
                                                </div>
                                            </div>

                                            <DatePicker label="İşe Başlama Tarihi *" value={values.iseBaslamaTarihi}
                                                onChange={(val) => setFieldValue("iseBaslamaTarihi", val)} disabled={isFormDisabled}
                                                slotProps={{ textField: { size: "small", fullWidth: true, className: "mb-3", error: !!hasError("iseBaslamaTarihi"), helperText: getErrorText("iseBaslamaTarihi") } }} />

                                            <TextField fullWidth select label="İzin Durumu" size="small" name="izinDurumu"
                                                value={values.izinDurumu} disabled>
                                                <MenuItem value={0}>İşlemde</MenuItem>
                                                <MenuItem value={1}>Onaya Gönderildi</MenuItem>
                                                <MenuItem value={2}>Onaylandı</MenuItem>
                                                <MenuItem value={3}>Red</MenuItem>
                                            </TextField>
                                        </div>
                                    </div>
                                </div>

                                {/* Sağ Kolon - Açıklamalar ve Onay İşlemleri */}
                                <div className="col-lg-5 col-md-6 mb-3">
                                    <div className="card h-100">
                                        <div className="card-body p-3 d-flex flex-column">
                                            <TextField fullWidth label="İzin Açıklama" multiline rows={6} size="small" name="izinAciklama"
                                                value={values.izinAciklama} onChange={handleChange} disabled={isFormDisabled} className="mb-3" />

                                            <TextField fullWidth label="İzin Adresi *" multiline rows={6} size="small" name="izinAdresi"
                                                value={values.izinAdresi} onChange={handleChange} disabled={isFormDisabled}
                                                error={!!hasError("izinAdresi")} helperText={getErrorText("izinAdresi")} className="mb-3" />

                                            {/* Vekalet Eden */}
                                            <TextField
                                                fullWidth
                                                label="Vekalet Eden"
                                                size="small"
                                                name="vekaletEden"
                                                value={values.vekaletEden}
                                                disabled={isFormDisabled}
                                                className="mb-2"
                                                InputProps={{
                                                    readOnly: true,
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => openPersonelPopup("vekaletEden")}
                                                                disabled={isFormDisabled}
                                                            >
                                                                <SearchIcon />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    )
                                                }}
                                            />
                                            <TextField
                                                fullWidth
                                                label="Vekalet Eden Ünvan"
                                                size="small"
                                                name="vekaletEdenUnvan"
                                                value={values.vekaletEdenUnvan}
                                                disabled={isFormDisabled}
                                                className="mb-3"
                                                InputProps={{ readOnly: true }}
                                            />
                                            <div className="d-flex gap-2 mt-auto">
                                                <Button
                                                    type="button"
                                                    variant="contained"
                                                    color="primary"
                                                    disabled={isOnayaGonderDisabled || isSaving}
                                                    onClick={handleOnayaGonder}
                                                    sx={{ flex: 1 }}
                                                >
                                                    {isSaving ? <CircularProgress size={20} color="inherit" /> : "Onaya Gönder"}
                                                </Button>
                                                <Button variant="outlined" color="secondary" disabled={!isKayitYapilmis} sx={{ flex: 1 }} onClick={handleYazdirClick}>
                                                    Yazdır
                                                </Button>
                                            </div>
                                            {!isKayitYapilmis && (
                                                <Alert severity="info" sx={{ mt: 2, fontSize: '0.75rem' }}>
                                                    Onaya göndermek için önce kaydı tamamlayın.
                                                </Alert>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Tablolar */}
                                <div className="col-lg-5 col-md-6 mt-4">
                                    <h6 className="mb-2 ms-2">İlgili İzinde Kullanılan Hakediş</h6>
                                    <div className="card-body p-0">
                                        <TableComponent columns={ilgiliHakedisColumns} data={ilgiliIzindeKullanilanHakedis} recordsPerPage={5} />
                                    </div>
                                </div>

                                <div className="col-lg-5 col-md-6 mt-4">
                                    <h6 className="mb-2 ms-2">Hakediş Bilgileri</h6>
                                    <div className="card-body p-0">
                                        <TableComponent columns={hakedisBilgileriColumns} data={hakedisBilgileri} recordsPerPage={5} />
                                    </div>
                                </div>
                            </div>

                            {/* Personel Arama Popup */}
                            <Dialog open={personelPopupOpen} onClose={() => setPersonelPopupOpen(false)} maxWidth="md" fullWidth>
                                <DialogTitle>
                                    Personel Ara
                                    <IconButton
                                        aria-label="close"
                                        onClick={() => setPersonelPopupOpen(false)}
                                        sx={{ position: 'absolute', right: 8, top: 8 }}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                </DialogTitle>
                                <DialogContent dividers>
                                    <div className="row mb-3">
                                        <div className="col-md-4 mb-2">
                                            <TextField
                                                fullWidth
                                                select
                                                label="Personel Türü"
                                                size="small"
                                                value={personelSearchForm.personelTuru}
                                                onChange={(e) => setPersonelSearchForm({ ...personelSearchForm, personelTuru: Number(e.target.value) })}
                                            >
                                                <MenuItem value={1}>Memur</MenuItem>
                                                <MenuItem value={2}>İşçi</MenuItem>
                                                <MenuItem value={3}>Taşeron</MenuItem>
                                                <MenuItem value={4}>Sözleşmeli</MenuItem>
                                            </TextField>
                                        </div>
                                        <div className="col-md-4 mb-2">
                                            <TextField
                                                fullWidth
                                                label="Adı"
                                                size="small"
                                                value={personelSearchForm.adi}
                                                onChange={(e) => setPersonelSearchForm({ ...personelSearchForm, adi: e.target.value })}
                                            />
                                        </div>
                                        <div className="col-md-4 mb-2">
                                            <TextField
                                                fullWidth
                                                label="Soyadı"
                                                size="small"
                                                value={personelSearchForm.soyadi}
                                                onChange={(e) => setPersonelSearchForm({ ...personelSearchForm, soyadi: e.target.value })}
                                            />
                                        </div>
                                        <div className="col-md-4 mb-2">
                                            <TextField
                                                fullWidth
                                                label="TC Kimlik No"
                                                size="small"
                                                value={personelSearchForm.tcKimlikNo}
                                                onChange={(e) => setPersonelSearchForm({ ...personelSearchForm, tcKimlikNo: e.target.value })}
                                            />
                                        </div>
                                        <div className="col-md-4 mb-2">
                                            <TextField
                                                fullWidth
                                                label="Kurum Sicil No"
                                                size="small"
                                                value={personelSearchForm.kurumSicilNo}
                                                onChange={(e) => setPersonelSearchForm({ ...personelSearchForm, kurumSicilNo: e.target.value })}
                                            />
                                        </div>
                                        <div className="col-md-4 mb-2">
                                            <TextField
                                                fullWidth
                                                select
                                                label="Çalışma Durumu"
                                                size="small"
                                                value={personelSearchForm.calismaDurumu}
                                                onChange={(e) => setPersonelSearchForm({ ...personelSearchForm, calismaDurumu: Number(e.target.value) })}
                                            >
                                                <MenuItem value={1}>Çalışanlar</MenuItem>
                                                <MenuItem value={2}>Ayrılanlar</MenuItem>
                                                <MenuItem value={3}>Hepsi</MenuItem>
                                            </TextField>
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <Button
                                            variant="contained"
                                            onClick={handlePersonelSearch}
                                            disabled={personelSearchLoading}
                                            startIcon={personelSearchLoading ? <CircularProgress size={16} /> : <SearchIcon />}
                                        >
                                            Ara
                                        </Button>
                                    </div>
                                    <div style={{ maxHeight: 300, overflow: 'auto' }}>
                                        <table className="table table-sm" style={{ borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr>
                                                    <th style={{ width: 40, backgroundColor: "#f2f2f2", borderRight: "1px solid #ddd" }}></th>
                                                    <th style={{ backgroundColor: "#f2f2f2", borderRight: "1px solid #ddd" }}>TC Kimlik No</th>
                                                    <th style={{ backgroundColor: "#f2f2f2", borderRight: "1px solid #ddd" }}>Kurum Sicil No</th>
                                                    <th style={{ backgroundColor: "#f2f2f2", borderRight: "1px solid #ddd" }}>Adı</th>
                                                    <th style={{ backgroundColor: "#f2f2f2" }}>Ünvanı</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {personelSearchResults.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={5} className="text-center">Kayıt Bulunamadı.</td>
                                                    </tr>
                                                ) : (
                                                    personelSearchResults
                                                        .slice((personelCurrentPage - 1) * personelRecordsPerPage, personelCurrentPage * personelRecordsPerPage)
                                                        .map((item: any, index: number) => {
                                                            const globalIndex = (personelCurrentPage - 1) * personelRecordsPerPage + index;
                                                            const handleSelect = () => {
                                                                setSelectedPersonelIndex(globalIndex);
                                                                if (personelPopupTarget === "vekaletEden") {
                                                                    setFieldValue("vekaletEden", item.adiSoyadi || '');
                                                                    setFieldValue("vekaletEdenId", item.rfPerSicilBilgi);
                                                                    setFieldValue("vekaletEdenUnvan", item.unvanTanimiAdi || '');
                                                                }
                                                                setPersonelPopupOpen(false);
                                                            };
                                                            const isSelected = selectedPersonelIndex === globalIndex;
                                                            return (
                                                                <tr
                                                                    key={globalIndex}
                                                                    onClick={handleSelect}
                                                                    style={{
                                                                        cursor: 'pointer',
                                                                        backgroundColor: isSelected ? '#1976d2' : undefined,
                                                                        color: isSelected ? '#fff' : undefined
                                                                    }}
                                                                    className={isSelected ? 'table-primary' : ''}
                                                                >
                                                                    <td style={{
                                                                        borderRight: "1px solid #ddd",
                                                                        backgroundColor: isSelected ? '#1976d2' : undefined
                                                                    }}>
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={handleSelect}
                                                                            sx={{
                                                                                color: isSelected ? '#fff' : '#1976d2'
                                                                            }}
                                                                        >
                                                                            <ArrowForwardIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </td>
                                                                    <td style={{
                                                                        borderRight: "1px solid #ddd",
                                                                        backgroundColor: isSelected ? '#1976d2' : undefined,
                                                                        color: isSelected ? '#fff' : undefined
                                                                    }}>{item.tcKimlikNo}</td>
                                                                    <td style={{
                                                                        borderRight: "1px solid #ddd",
                                                                        backgroundColor: isSelected ? '#1976d2' : undefined,
                                                                        color: isSelected ? '#fff' : undefined
                                                                    }}>{item.qtKurumSicilNo}</td>
                                                                    <td style={{
                                                                        borderRight: "1px solid #ddd",
                                                                        backgroundColor: isSelected ? '#1976d2' : undefined,
                                                                        color: isSelected ? '#fff' : undefined
                                                                    }}>{item.adiSoyadi}</td>
                                                                    <td style={{
                                                                        backgroundColor: isSelected ? '#1976d2' : undefined,
                                                                        color: isSelected ? '#fff' : undefined
                                                                    }}>{item.uniteAdi}</td>
                                                                </tr>
                                                            );
                                                        })
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                    {personelSearchResults.length > personelRecordsPerPage && (
                                        <Box mt={2} display="flex" justifyContent="center">
                                            <Pagination
                                                count={Math.ceil(personelSearchResults.length / personelRecordsPerPage)}
                                                page={personelCurrentPage}
                                                onChange={(_, value) => {
                                                    setPersonelCurrentPage(value);
                                                    setSelectedPersonelIndex(null);
                                                }}
                                                color="primary"
                                                size="small"
                                            />
                                        </Box>
                                    )}
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={() => setPersonelPopupOpen(false)}>Kapat</Button>
                                </DialogActions>
                            </Dialog>

                            {/* Yazdır Popup */}
                            <Dialog open={yazdirPopupOpen} onClose={() => setYazdirPopupOpen(false)} maxWidth="xs" fullWidth>
                                <DialogTitle>
                                    İzin Formu Seçimi
                                    <IconButton
                                        aria-label="close"
                                        onClick={() => setYazdirPopupOpen(false)}
                                        sx={{ position: 'absolute', right: 8, top: 8 }}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                </DialogTitle>
                                <DialogContent>
                                    <TextField
                                        select
                                        fullWidth
                                        label="İzin Formu"
                                        size="small"
                                        value={selectedIzinFormu}
                                        onChange={(e) => setSelectedIzinFormu(Number(e.target.value))}
                                        sx={{ mt: 1 }}
                                        SelectProps={{
                                            MenuProps: {
                                                disablePortal: true
                                            }
                                        }}
                                    >
                                        {isMemur() ? [
                                            <MenuItem key={1} value={1}>Memur İzin Formu</MenuItem>,
                                            <MenuItem key={2} value={2}>Müdür İzin Formu</MenuItem>,
                                            <MenuItem key={3} value={3}>Başkan Y. İzin Formu</MenuItem>
                                        ] : (
                                            <MenuItem key={4} value={4}>İşçi Yıllık İzin Formu</MenuItem>
                                        )}
                                    </TextField>
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={() => setYazdirPopupOpen(false)}>İptal</Button>
                                    <Button
                                        variant="contained"
                                        onClick={handleYazdirConfirm}
                                        disabled={yazdirLoading}
                                    >
                                        {yazdirLoading ? <CircularProgress size={20} color="inherit" /> : "Yazdır"}
                                    </Button>
                                </DialogActions>
                            </Dialog>
                        </Form>
                    );
                }}
            </Formik>
        </>
    );
};

export default IzinBasvuru;